import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Dropdown, Row } from "react-bootstrap";
import { generatePath, Link, useParams } from "react-router-dom";
import { debounce } from "lodash";
import moment from "moment";
import DatePicker from "react-datepicker";
import { useGetSchoolClientsListQuery } from "store/services/clients/school.client";
import { useGetHomeEducationListQuery } from "store/services/clients/homeEducation.client";
import { useGetPrivateClientListQuery } from "store/services/clients/private.client";
import { useGetClientChatterListQuery } from "store/services/clients/chatter.client";
import { useGetSchoolDivisonListForFilterQuery } from "store/services/schoolsDivisons";
import { useGetFundingSourceListQuery, useLazyProgramLookupQuery } from "store/services/utility";
import { useGetTherapistDataQuery } from "store/services/therapistProfiles";
import { useBulkChangeClientStatusMutation } from "store/services/client";
import { useBulkRemoveTherapistCaseloadMutation } from "store/services/clientCaseLoads";
import TableLayoutV2 from "components/TableLayoutV2";
import LoadingOverlay from "components/shared/LoadingOverlay";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import { Breadcrumbs, CommonSelect, CstmPagination } from "components/Common/Common";
import { convertToMomentDate, successMessageToast, userFullName } from "helpers/common";
import confirmDialog from "utils/confirmationDialog";
import { RightArrowIcon } from "Assets/icons/V2";
import { BULK_CLIENT_STATUS_OPTIONS, CHATTER_CLIENT_STATUS_OPTION, CLIENT_STATUS_OPTION, GRADE_OPTIONS, HOME_CLIENT_STATUS_OPTION } from "config/constants";
import { THERAPIST_DETAIL, THERAPIST_EDIT } from "config/paths";

const tabs = [
    { key: "schools", label: "Schools" },
    { key: "school", label: "School Clients" },
    { key: "home", label: "Home Ed Clients" },
    { key: "private", label: "Private Clients" },
    { key: "chatter", label: "Chatter Clients" },
];

const initialFilter = {
    search: "",
    division: "",
    school: "",
    grade: "",
    startDate: "",
    endDate: "",
    start_date: "",
    end_date: "",
    education_authority: "",
    source: "",
    funding_source: "",
    program_id: "",
    status: { label: "Active", value: "active" },
    sort: { column: "lastname", direction: "asc" },
};

const getClientValue = (client, key) => client?.[key] ?? client?.client_detail?.[key] ?? "";

// Tab key -> the client type segment the bulk status endpoint expects
const CLIENT_TYPE_BY_TAB = {
    school: "school",
    home: "home-education",
    private: "private",
    chatter: "chatter",
};

// Plain input rather than Form.Check so the indeterminate state can be set on the element
const SelectCheckbox = ({ checked, indeterminate = false, onChange }) => {
    const inputRef = useRef(null);
    useEffect(() => {
        if (inputRef.current) inputRef.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return <input
        ref={inputRef}
        type="checkbox"
        className="m-0 assigned-client-checkbox"
        checked={checked}
        onChange={onChange}
        onClick={(event) => event.stopPropagation()}
    />;
};

const AssignedClientsToTherapist = () => {
    const { id: therapistId } = useParams();
    const [activeTab, setActiveTab] = useState("schools");
    const [filter, setFilter] = useState(initialFilter);
    const [currentPage, setCurrentPage] = useState(1);
    // Selection survives paging and filtering so a clinician can be cleared out across several pages
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkChangeStatus, { isLoading: bulkStatusLoading }] = useBulkChangeClientStatusMutation();
    const [bulkRemoveFromCaseload, { isLoading: bulkRemoveLoading }] = useBulkRemoveTherapistCaseloadMutation();
    const pageSize = 10;
    const { data: schoolDivisions, isLoading: schoolDivisionsLoading } = useGetSchoolDivisonListForFilterQuery({}, { skip: activeTab !== "school" });
    const { data: fundingSources, isLoading: fundingSourcesLoading } = useGetFundingSourceListQuery();
    const [getProgramNames, { isLoading: programNamesLoading }] = useLazyProgramLookupQuery();
    const [programOptions, setProgramOptions] = useState([]);
    const { data: therapistData, isLoading: therapistLoading, isError: therapistError } = useGetTherapistDataQuery(therapistId, { skip: !therapistId });
    const request = { page: currentPage, limit: pageSize, therapist_id: Number(therapistId), ...filter };

    const school = useGetSchoolClientsListQuery(request, { skip: activeTab !== "school" });
    const home = useGetHomeEducationListQuery(request, { skip: activeTab !== "home" });
    const privateClients = useGetPrivateClientListQuery(request, { skip: activeTab !== "private" });
    const chatter = useGetClientChatterListQuery(request, { skip: activeTab !== "chatter" });
    const activeRequest = { school, home, private: privateClients, chatter }[activeTab];
    const rows = activeRequest?.data?.records || activeRequest?.data?.data || activeRequest?.data?.items || [];
    const totalPages = Math.ceil((activeRequest?.data?.totalRecords || 0) / pageSize);

    const pageIds = useMemo(() => rows.map((row) => row?.id).filter(Boolean), [rows]);
    const selectedCount = selectedIds.length;
    const hasSelection = selectedCount > 0;
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
    const somePageSelected = pageIds.some((id) => selectedIds.includes(id));
    const bulkLoading = bulkStatusLoading || bulkRemoveLoading;

    const clearSelection = () => setSelectedIds([]);
    const toggleClient = (id) => setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
    const togglePage = () => setSelectedIds((current) => allPageSelected
        ? current.filter((id) => !pageIds.includes(id))
        : [...new Set([...current, ...pageIds])]);

    const runBulkAction = async (action, confirmTitle, confirmBtnText, successMessage) => {
        const isConfirm = await confirmDialog({ title: confirmTitle, confirmBtnText });
        if (!isConfirm) return;
        const result = await action();
        // axiosBaseQuery already surfaces the server message on failure
        if (result?.error) return;
        successMessageToast(successMessage);
        clearSelection();
        // The mutations invalidate the list tag, which refreshes the visible page and drops
        // the cache entries held for the other filter values.
    };

    const handleBulkRemove = () => runBulkAction(
        () => bulkRemoveFromCaseload({ clientType: CLIENT_TYPE_BY_TAB[activeTab], therapist_id: Number(therapistId), client_ids: selectedIds }),
        `Remove ${selectedCount} ${selectedCount === 1 ? "client" : "clients"} from this clinician?`,
        "Remove",
        `${selectedCount} ${selectedCount === 1 ? "client" : "clients"} removed from this clinician.`
    );

    const handleBulkStatus = (option) => runBulkAction(
        () => bulkChangeStatus({ clientType: CLIENT_TYPE_BY_TAB[activeTab], client_ids: selectedIds, status: option.value }),
        `Set ${selectedCount} ${selectedCount === 1 ? "client" : "clients"} to ${option.label}?`,
        "Change Status",
        `${selectedCount} ${selectedCount === 1 ? "client" : "clients"} set to ${option.label}.`
    );

    const assignedSchools = useMemo(() => {
        const divisions = {};
        therapistData?.school_therapists?.forEach((assignment) => {
            const division = assignment?.divisions;
            const school = assignment?.schools;
            if (!division?.id || !school?.id) return;
            if (!divisions[division.id]) {
                divisions[division.id] = {
                    divisionId: division.id,
                    divisionTitle: division.title,
                    schools: [],
                };
            }
            if (!divisions[division.id].schools.some((item) => item.id === school.id)) {
                divisions[division.id].schools.push({ id: school.id, name: school.name });
            }
        });
        return Object.values(divisions);
    }, [therapistData?.school_therapists]);

    const updateFilter = (key, value) => {
        setFilter((current) => ({ ...current, [key]: value }));
        setCurrentPage(1);
    };
    const handleSearch = useMemo(() => debounce((value) => updateFilter("search", value), 400), []);
    const handleTabChange = (key) => {
        setActiveTab(key);
        setFilter(initialFilter);
        setCurrentPage(1);
        // Ids belong to one client type, so they cannot carry across tabs
        clearSelection();
    };

    const handleProgramSearch = async (search) => {
        const result = await getProgramNames(search);
        if (!result?.error) setProgramOptions(result?.data?.map((item) => ({ label: item?.name, value: item?.id })) || []);
    };

    const schoolOptions = useMemo(() => schoolDivisions?.reduce((schools, division) => [...schools, ...(division?.schools || [])], []) || [], [schoolDivisions]);

    const renderFilter = (label, children, className = "") => (
        <Col sm="6" md="2" className={className}>
            <div className="w-100 align-items-center gap-3">
                <label className="discharge-screen-label mb-0">{label}</label>
                {children}
            </div>
        </Col>
    );

    const dateFilter = (key, label) => renderFilter(label, <DatePicker
        className="form-control"
        placeholderText={label}
        selected={filter[key] ? new Date(filter[key]) : null}
        onChange={(date) => updateFilter(key, date ? moment(date).format("YYYY-MM-DD") : "")}
        isClearable
    />);

    const columns = useMemo(() => {
        const common = [
            { head: "LAST NAME", accessor: "lastname", sorter: true, component: (item) => <span>{getClientValue(item, "lastname") || "--"}</span> },
            { head: "FIRST NAME", accessor: "firstname", sorter: true, component: (item) => <span>{getClientValue(item, "firstname") || "--"}</span> },
            { head: "DOB", accessor: "dob", component: (item) => convertToMomentDate(getClientValue(item, "dob")) || "--" },
        ];
        const typeColumns = {
            schools: [],
            school: [
                { head: "GRADE", accessor: "grade", component: (item) => getClientValue(item, "grade") || "--" },
                { head: "SCHOOL", accessor: "school", component: (item) => item?.client_detail?.client_school_detail?.client_school?.name || item?.school?.name || "--" },
                { head: "DISCIPLINES", accessor: "disciplines", component: (item) => item?.clientCaseloads?.[0]?.clientCaseloadTherapist?.map((entry) => entry?.caseDiscipline?.assign_term).filter(Boolean).join(", ") || "--" },
            ],
            home: [
                { head: "EDUCATION AUTHORITY", accessor: "education_authority", component: (item) => item?.client_detail?.client_home_education_detail?.education_authority || "--" },
                { head: "DATE OF INTAKE", accessor: "intakeDate", component: (item) => convertToMomentDate(item?.client_detail?.client_home_education_detail?.intake_date) || "--" },
                { head: "SOURCE", accessor: "source", component: (item) => item?.client_detail?.source || "--" },
            ],
            private: [
                { head: "SUBMITTED DATE", accessor: "submittedDate", component: (item) => convertToMomentDate(item?.createdAt) || "--" },
                { head: "FUNDING SOURCE", accessor: "funding_source", component: (item) => item?.client_detail?.client_private_detail?.private_funding_source?.name || "--" },
                { head: "DISCIPLINE", accessor: "discipline", component: (item) => item?.clientCaseloads?.[0]?.clientCaseloadTherapist?.map((entry) => entry?.caseDiscipline?.assign_term).filter(Boolean).join(", ") || "--" },
            ],
            chatter: [
                { head: "DATE OF INTAKE", accessor: "RegistrationDate", component: (item) => convertToMomentDate(item?.createdAt) || "--" },
                { head: "FUNDING SOURCE", accessor: "funding_source", component: (item) => item?.client_detail?.client_chatter_detail?.chatter_funding_source?.name || "--" },
            ],
        };
        return [...common, ...typeColumns[activeTab], { head: "ACCOUNT STATUS", accessor: "status", sorter: true, component: (item) => <span style={{textTransform: "capitalize"}} className={item?.status === "active" ? "greenStatus" : "text-muted"}>{item?.status || "--"}</span> },
        //  { head: "", accessor: "actions", component: () => <RightArrowIcon /> }
        ];
    }, [activeTab]);

    const tableColumns = useMemo(() => {
        if (activeTab === "schools") return columns;
        return [{
            head: <SelectCheckbox checked={allPageSelected} indeterminate={!allPageSelected && somePageSelected} onChange={togglePage} />,
            accessor: "select",
            component: (item) => <SelectCheckbox checked={selectedIds.includes(item?.id)} onChange={() => toggleClient(item?.id)} />,
        }, ...columns];
    }, [activeTab, columns, selectedIds, allPageSelected, somePageSelected, pageIds]);

    return (
        <section style={{paddingLeft: "10px"}} className="ManageProfile position-relative py-2">
            <Container fluid>
                <Row>
                    <Breadcrumbs themeBlueV2 items={[{ label: "Clinicians", path: "/manage-team/therapist-profiles" }, { label: userFullName(therapistData) }]} />
                    <Col lg="12" className="mt-4">
                        <div style={{ borderBottom: "1px solid #315d99" }} className=" d-flex justify-content-between align-items-end  mb-3">
                            <h6 style={{ color: "#315D99" }} className="m-0 fw-bold ">CLINICIAN INFORMATION</h6>
                            <Link style={{ color: "#315D99" }} className="assigned-client-details-link small" to={generatePath(THERAPIST_DETAIL, { id: therapistId })}>SEE USER DETAILS</Link>
                        </div>
                        <div className="d-flex border-bottom mb-3 assigned-client-tabs">
                            {tabs.map((tab) => <button type="button" key={tab.key} className={`assigned-client-tab ${activeTab === tab.key ? "active" : ""}`} onClick={() => handleTabChange(tab.key)}>{tab.label}</button>)}
                        </div>
                        {activeTab !== "schools" && <Row className="reports-v2 my-3 d-flex justify-content-end classroom-conslutation">
                            {activeTab === "school" && <>
                                {renderFilter("School Division", <CommonSelect placeholder="Choose one..." option={schoolDivisions?.map((item) => ({ label: item?.title, value: item?.id }))} value={filter.division} onChange={(value) => updateFilter("division", value)} isFilterV2 isClearable isLoading={schoolDivisionsLoading} />)}
                                {renderFilter("School", <CommonSelect placeholder="Choose one..." option={schoolOptions.map((item) => ({ label: item?.name, value: item?.id }))} value={filter.school} onChange={(value) => updateFilter("school", value)} isFilterV2 isClearable isLoading={schoolDivisionsLoading} />)}
                                {renderFilter("Grade", <CommonSelect placeholder="Select Grade" option={GRADE_OPTIONS} value={filter.grade} onChange={(value) => updateFilter("grade", value)} isFilterV2 isClearable />)}
                            </>}
                            {activeTab === "home" && <>
                                {dateFilter("startDate", "Start Date")}
                                {dateFilter("endDate", "End Date")}
                                {renderFilter("Source", <CommonSelect placeholder="Select" option={[{ label: "Internal", value: "internal" }, { label: "Web", value: "web" }]} value={filter.source} onChange={(value) => updateFilter("source", value)} isFilterV2 isClearable />)}
                                {renderFilter("Education Authority", <input onChange={(event) => updateFilter("education_authority", event.target.value)} className="input-new readonly-box h-auto search-input-filter-v2" placeholder="Find authority..." defaultValue={filter.education_authority} />)}
                            </>}
                            {activeTab === "private" && <>
                                {renderFilter("Funding Source", <CommonSelect placeholder="Choose one..." option={fundingSources?.map((item) => ({ label: item?.name, value: item?.id }))} value={filter.funding_source} onChange={(value) => updateFilter("funding_source", value)} isFilterV2 isClearable isLoading={fundingSourcesLoading} />)}
                                {dateFilter("start_date", "Start Date")}
                                {dateFilter("end_date", "End Date")}
                            </>}
                            {activeTab === "chatter" && <>
                                {renderFilter("Program Name", <CommonSelect placeholder="Choose one..." option={programOptions} value={filter.program_id} onChange={(value) => updateFilter("program_id", value)} onInputChange={handleProgramSearch} isFilterV2 isClearable isLoading={programNamesLoading} />)}
                                {renderFilter("Funding Source", <CommonSelect placeholder="Select" option={fundingSources?.map((item) => ({ label: item?.name, value: item?.id }))} value={filter.funding_source} onChange={(value) => updateFilter("funding_source", value)} isFilterV2 isClearable isLoading={fundingSourcesLoading} />)}
                            </>}
                            {renderFilter("Status", <CommonSelect placeholder="Select" option={activeTab === "home" ? HOME_CLIENT_STATUS_OPTION : activeTab === "chatter" ? CHATTER_CLIENT_STATUS_OPTION : CLIENT_STATUS_OPTION} value={filter.status} onChange={(value) => updateFilter("status", value)} isFilterV2 />)}
                            {renderFilter("Search", <input onChange={(event) => handleSearch(event.target.value)} className="input-new readonly-box h-auto search-input-filter-v2" placeholder="Find a client..." defaultValue={filter.search} />, "border-start ps-3")}
                        </Row>}
                        {activeTab === "schools" ? (
                            therapistLoading ? <div style={{ height: 200 }}><LoadingIndicator /></div> : therapistError ? <ErrorComponent /> : (
                                <div className="assigned-schools-list">
                                    <div className="d-flex justify-content-end mb-3">
                                        <Link className="assigned-schools-edit" to={generatePath(THERAPIST_EDIT, { id: therapistId })}>Edit</Link>
                                    </div>
                                    {assignedSchools.length ? assignedSchools.map((division) => (
                                        <Row className="assigned-school-row" key={division.divisionId}>
                                            <Col xs="6" md="4">
                                                <label>School Divisions</label>
                                                <p style={{color: "black"}}>{division.divisionTitle}</p>
                                            </Col>
                                            <Col xs="6" md="4">
                                                <label>Schools</label>
                                                {division.schools.map((school) => <p style={{color: "black"}} key={school.id}>{school.name}</p>)}
                                            </Col>
                                        </Row>
                                    )) : <div className="assigned-schools-empty">No Record Found</div>}
                                </div>
                            )
                        ) : (
                            <div className="table-outer position-relative">
                                {hasSelection && <div className="assigned-client-bulkbar">
                                    <div className="d-flex align-items-center gap-3">
                                        <SelectCheckbox checked={allPageSelected} indeterminate={!allPageSelected && somePageSelected} onChange={togglePage} />
                                        <button type="button" className="assigned-client-bulk-action" onClick={handleBulkRemove} disabled={bulkLoading}>Remove from list</button>
                                        <span className="assigned-client-bulk-count">
                                            {selectedCount} selected
                                            <button type="button" className="assigned-client-bulk-clear" onClick={clearSelection} disabled={bulkLoading}>Clear</button>
                                        </span>
                                    </div>
                                    <Dropdown align="end">
                                        <Dropdown.Toggle as="button" type="button" className="assigned-client-bulk-action" disabled={bulkLoading}>Change Status</Dropdown.Toggle>
                                        <Dropdown.Menu className="assigned-client-bulk-menu">
                                            {BULK_CLIENT_STATUS_OPTIONS.map((option) => <Dropdown.Item key={option.value} onClick={() => handleBulkStatus(option)}>{option.label}</Dropdown.Item>)}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>}
                                {activeRequest?.isLoading ? <div style={{ height: 200 }}><LoadingIndicator /></div> : activeRequest?.isError ? <ErrorComponent /> : <>{(activeRequest?.isFetching || bulkLoading) && <LoadingOverlay />}<TableLayoutV2 data={rows} column={tableColumns} showHeader={!hasSelection} filter={filter} handleSort={(sort) => updateFilter("sort", sort)} /></>}
                                <CstmPagination totalPages={totalPages} activePage={currentPage} className="paginationV2" handlePageChange={setCurrentPage} />
                            </div>
                        )}
                    </Col></Row>
            </Container>
        </section>
    );
};

export default AssignedClientsToTherapist;